import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
    CANCEL_SPEC_INTELLIGENCE_RUN,
    START_SPEC_INTELLIGENCE_RUN,
} from 'api/graphQL/mutations/specIntelligence.mutations'
import {
    GET_LATEST_SPEC_INTELLIGENCE_REPORT,
    GET_SPEC_INTELLIGENCE_REPORT,
} from 'api/graphQL/queries/specIntelligence.queries'
import type {
    CancelSpecIntelligenceRunInput,
    CancelSpecIntelligenceRunResponse,
    FindingResolutionState,
    GetLatestSpecIntelligenceReportInput,
    GetLatestSpecIntelligenceReportResponse,
    GetSpecIntelligenceReportInput,
    GetSpecIntelligenceReportResponse,
    SpecIntelligenceReport,
    SpecIntelligenceRunStatus,
    StartSpecIntelligenceRunInput,
    StartSpecIntelligenceRunResponse,
} from 'api/graphQL/types/specIntelligence.types'
import { useCallback, useEffect, useRef, useState } from 'react'
import useSpecIntelligencePolling from './useSpecIntelligencePolling'

export type SpecIntelligenceDrawerView = 'loading' | 'picker' | 'starting' | 'progress' | 'results' | 'error'

/** Returned by {@link useSpecIntelligence}. */
export interface UseSpecIntelligenceResult {
    /** Current view for the drawer UI. */
    drawerView: SpecIntelligenceDrawerView
    /** True while a run is building — drives the icon animation. */
    isBuilding: boolean
    /** Latest polling status; null when idle. */
    runStatus: SpecIntelligenceRunStatus | null
    /** Completed report for the results view; null when unavailable. */
    currentReport: SpecIntelligenceReport | null
    /** Section numbers from the report that triggered re-run — pre-populates the picker. */
    rerunSectionNumbers: string[] | null
    /** Trigger a new analysis run with the given section IDs. */
    startRun: (selectedLeafIds: string[]) => Promise<void>
    /** Cancel the in-progress run, discard its partial report, and return to the picker. */
    cancelRun: () => Promise<void>
    /** True while the cancel mutation is in flight — disables the cancel action. */
    isCanceling: boolean
    /** Reset back to the picker view after viewing results. */
    handleRerun: () => void
    /**
     * Sync a finding's resolution state into the current report.
     * Call on optimistic resolve/dismiss and on rollback so the report stays
     * accurate across drawer close/reopen cycles.
     */
    syncFindingState: (id: string, state: FindingResolutionState) => void
}

/**
 * Owns all SpecIntelligence API interactions: initial report check, run-status polling,
 * final report fetch, and the start-run mutation. Components receive data and callbacks
 * via the returned object — no GraphQL in the UI layer.
 *
 * @param projectId - The project whose SpecIntelligence data to load.
 * @param skip - When true, all queries and mutations are suppressed (e.g. drawer not visible).
 */
const useSpecIntelligence = (projectId: string, skip = false): UseSpecIntelligenceResult => {
    const [drawerView, setDrawerView] = useState<SpecIntelligenceDrawerView>('loading')
    const [currentCompleteReport, setCurrentCompleteReport] = useState<SpecIntelligenceReport | null>(null)
    const [rerunSectionNumbers, setRerunSectionNumbers] = useState<string[] | null>(null)
    const [isCanceling, setIsCanceling] = useState(false)

    // Polling (survives drawer close / tab switches)
    const { isBuilding, pollingInstanceId, runStatus, startPolling, resetPolling } =
        useSpecIntelligencePolling(skip || !projectId)

    // Instance ids the user has cancelled. A cancelled run must never be polled again, even if a
    // later report read still reports it as Running (a failed cancel, or a read that raced the
    // backend's delete of the in-progress report).
    const cancelledInstanceIds = useRef<Set<string>>(new Set())

    // Initial report query on render to check for existing data
    const { loading: initialReportLoading, data: initialReportData, refetch: refetchLatestReport } = useQuery<
        GetLatestSpecIntelligenceReportResponse,
        GetLatestSpecIntelligenceReportInput
    >(GET_LATEST_SPEC_INTELLIGENCE_REPORT, {
        variables: { input: { projectId, includeResolved: true } },
        fetchPolicy: 'network-only',
        skip: skip || !projectId,
    })

    // Manual fetch for completed report after user (re)-runs analysis
    const [fetchReport, { loading: fetchedReportLoading, data: fetchedReportData }] = useLazyQuery<
        GetSpecIntelligenceReportResponse,
        GetSpecIntelligenceReportInput
    >(GET_SPEC_INTELLIGENCE_REPORT, { fetchPolicy: 'network-only' })

    const applyReport = useCallback((report: SpecIntelligenceReport) => {
        if (report.status === 'Complete') {
            setCurrentCompleteReport(report)
            setDrawerView('results')
        } else if (report.status === 'Failed') {
            setCurrentCompleteReport(report)
            setDrawerView('error')
        }
    }, [])

    // Reset to loading whenever queries are suppressed
    useEffect(() => {
        if (skip || !projectId) setDrawerView('loading')
    }, [skip, projectId])

    // Reset all local state and stop any in-flight polling when the project changes,
    // so a report or active run from the previous project can't leak into the new one.
    useEffect(() => {
        setDrawerView('loading')
        setCurrentCompleteReport(null)
        setRerunSectionNumbers(null)
        cancelledInstanceIds.current.clear()
        resetPolling()
    }, [projectId, resetPolling])

    // React to initial report load
    useEffect(() => {
        if (skip || !projectId || initialReportLoading) return

        const report = initialReportData?.getLatestSpecIntelligenceReport
        if (!report) {
            // Query completed but no prior report — let the user run a fresh analysis
            if (initialReportData) setDrawerView('picker')
            return
        }
        if (report.status === 'Running' && report.instanceId) {
            // A run the user already cancelled must not drag them back into the progress view.
            if (cancelledInstanceIds.current.has(report.instanceId)) {
                setDrawerView('picker')
                return
            }
            startPolling(report.instanceId)
            setDrawerView('progress')
        } else {
            applyReport(report)
        }
    }, [initialReportLoading, initialReportData, skip, projectId, startPolling, applyReport])

    // React to report data from manual fetch after polling completes
    useEffect(() => {
        if (skip || !projectId || fetchedReportLoading) return
        const report = fetchedReportData?.getSpecIntelligenceReport
        if (report) applyReport(report)
    }, [fetchedReportLoading, fetchedReportData, skip, projectId, applyReport])

    // React to polling reaching Complete or Error. Stay in the progress view on Error too — the
    // report fetch below carries the real errorMessage/errorType, so wait for it (applyReport)
    // rather than showing the generic ErrorView early.
    useEffect(() => {
        if (!runStatus) return
        const reportId = runStatus.result?.reportId
        if (reportId && (runStatus.state === 'Complete' || runStatus.state === 'Error')) {
            fetchReport({
                variables: { input: { reportId, includeResolved: true } },
            })
        } else if (runStatus.state === 'Error') {
            // No report to fetch (e.g. run failed before producing one) — fall back to the
            // generic error message instead of hanging in the progress view forever.
            setCurrentCompleteReport(null)
            setDrawerView('error')
        }
    }, [runStatus, fetchReport])

    // Start run mutation
    const [startRunMutation] = useMutation<
        StartSpecIntelligenceRunResponse,
        StartSpecIntelligenceRunInput
    >(START_SPEC_INTELLIGENCE_RUN)

    const startRun = useCallback(
        async (selectedLeafIds: string[]) => {
            setDrawerView('starting')
            try {
                const result = await startRunMutation({
                    variables: { input: { projectId, projectProductFamilyIds: selectedLeafIds } },
                })
                const instanceId = result.data?.startSpecIntelligenceRun?.instanceId
                if (instanceId) {
                    startPolling(instanceId)
                    setDrawerView('progress')
                } else {
                    setDrawerView('picker')
                }
            } catch {
                setDrawerView('error')
            }
        },
        [startRunMutation, projectId, startPolling]
    )

    // Cancel run mutation
    const [cancelRunMutation] = useMutation<
        CancelSpecIntelligenceRunResponse,
        CancelSpecIntelligenceRunInput
    >(CANCEL_SPEC_INTELLIGENCE_RUN)

    const cancelRun = useCallback(async () => {
        if (!pollingInstanceId) return
        const instanceId = pollingInstanceId
        setIsCanceling(true)

        // False both when the backend had nothing left to cancel and when the request failed —
        // either way the run may have produced a report we should not hide from the user.
        let wasCancelled = false
        try {
            // The backend discards the in-progress report, so the project's latest report reverts
            // to the last completed run.
            const result = await cancelRunMutation({ variables: { input: { instanceId } } })
            wasCancelled = result.data?.cancelSpecIntelligenceRun ?? false
        } catch (error) {
            // Deliberately not surfaced to the user: a failed cancel only means work may continue
            // server-side, which is not their problem — what matters is that they get out of the
            // progress view and can start a new run. Logged so monitoring can see the failure rate.
            console.error('[useSpecIntelligence] cancelRun mutation failed:', error)
        }

        cancelledInstanceIds.current.add(instanceId)

        // React 17 does not batch updates after an await, so order these newest-view-first:
        // the intermediate renders then already show the picker rather than flashing a reset
        // progress bar and a re-enabled Cancel button.
        setDrawerView('picker')
        resetPolling()
        setIsCanceling(false)

        if (!wasCancelled) {
            // The run had already finished (or the cancel never landed). Re-read the latest report
            // so a completed or failed run still surfaces instead of being silently discarded.
            // The initial-report effect routes the result; a cancelled-instance Running report is
            // filtered there, so this cannot restart polling.
            refetchLatestReport()?.catch(() => undefined)
        }
    }, [cancelRunMutation, pollingInstanceId, refetchLatestReport, resetPolling])

    const handleRerun = useCallback(() => {
        setRerunSectionNumbers(currentCompleteReport?.sectionNumbers ?? null)
        setCurrentCompleteReport(null)
        setDrawerView('picker')
    }, [currentCompleteReport])

    const syncFindingState = useCallback((id: string, state: FindingResolutionState) => {
        setCurrentCompleteReport((prev) =>
            prev
                ? {
                    ...prev,
                    findings: prev.findings.map((f) =>
                        f.id === id ? { ...f, resolutionState: state } : f
                    ),
                }
                : null
        )
    }, [])

    return {
        drawerView,
        isBuilding,
        runStatus,
        currentReport: currentCompleteReport,
        rerunSectionNumbers,
        startRun,
        cancelRun,
        isCanceling,
        handleRerun,
        syncFindingState,
    }
}

export default useSpecIntelligence
